import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { PageContainer } from "@/layouts/PageContainer";
import { Tabs } from "@/components/ui/Tabs";
import { StatCard } from "@/components/cards/StatCard";
import { DataTable } from "@/components/tables/DataTable";
import { Badge } from "@/components/ui/Badge";
import { LoadingState } from "@/components/feedback/LoadingState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { CommissionPolicyCard } from "./CommissionPolicyCard";
import { PayoutsCard } from "./PayoutsCard";
import { useCommissionOverview } from "@/features/commission/useCommission";
import { formatCompactINR, formatINR, formatNumber } from "@/utils/format";
import type { CommissionLedgerRow } from "@/types/domain";

const PERIOD_TABS = [
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
];

function periodRange(period: "week" | "month"): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  const from = new Date(now);
  if (period === "week") from.setDate(now.getDate() - 7);
  else from.setDate(1);
  return { from: from.toISOString().slice(0, 10), to };
}

export default function Commission() {
  const [period, setPeriod] = useState<"week" | "month">("week");
  const range = periodRange(period);
  const overview = useCommissionOverview(range);

  const columns = useMemo<ColumnDef<CommissionLedgerRow, unknown>[]>(
    () => [
      { header: "Driver", accessorKey: "driverName" },
      { header: "Rides", accessorFn: (r) => formatNumber(r.rides) },
      { header: "Gross (GMV)", accessorFn: (r) => formatINR(r.gross) },
      {
        header: "Commission",
        accessorFn: (r) => `${formatINR(r.commission)} (${Math.round(r.commissionPct * 100)}%)`,
      },
      { header: "Collected", accessorFn: (r) => formatINR(r.collected) },
      { header: "Pending", accessorFn: (r) => formatINR(r.pending) },
      {
        header: "",
        id: "status",
        cell: ({ row }) =>
          row.original.settled ? (
            <Badge tone="teal">Settled</Badge>
          ) : (
            <Badge tone="danger">Pending</Badge>
          ),
      },
    ],
    [],
  );

  return (
    <PageContainer
      title="Commission"
      description="Commission is charged at withdrawal, based on each driver's weekly ride total."
      actions={<Tabs items={PERIOD_TABS} value={period} onChange={(v) => setPeriod(v as typeof period)} />}
    >
      <CommissionPolicyCard />
      <PayoutsCard />
      {overview.isLoading && <LoadingState label="Loading commission overview…" />}
      {overview.error && <ErrorState error={overview.error} onRetry={() => overview.refetch()} />}
      {overview.data && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            value={formatCompactINR(overview.data.totals.commission)}
            label="Commission earned"
            highlight
          />
          <StatCard value={formatCompactINR(overview.data.totals.collected)} label="Collected" />
          <StatCard value={formatCompactINR(overview.data.totals.pending)} label="Pending" />
          <StatCard value={formatNumber(overview.data.totals.rides)} label="Rides" />
        </div>
      )}

      <div className="card">
        <DataTable
          columns={columns}
          data={overview.data?.items ?? []}
          isLoading={overview.isLoading}
          error={overview.error}
          onRetry={() => overview.refetch()}
          rowKey={(r) => r.driverId}
          emptyTitle="No commission activity for this period"
        />
      </div>
      <p className="text-xs text-ink-faint">
        The policy above is stored by the commission service; the frontend never modifies
        financial records directly.
      </p>
    </PageContainer>
  );
}
